import { useCallback, useEffect, useRef, useState } from "react";
import type { MediaAsset } from "../domain/types";
import {
  MediaValidationError,
  createAndUploadMedia,
  formatBytes,
} from "../media/createMediaAsset";
import { VIDEO_ACCEPT, VIDEO_MAX_SECONDS } from "../media/limits";
import { uploadStatusLabel, uploadUserMessage } from "../media/uploadMessages";
import { useT } from "../i18n";

type RecState = "idle" | "preview" | "recording" | "unsupported";

export function VideoCapturePanel({
  asset,
  userId,
  onChange,
}: {
  asset: MediaAsset | null;
  userId: string;
  onChange: (asset: MediaAsset | null) => void;
}) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraFileRef = useRef<HTMLInputElement>(null);
  const liveRef = useRef<HTMLVideoElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const [recState, setRecState] = useState<RecState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (liveRef.current) liveRef.current.srcObject = null;
  }, []);

  useEffect(() => () => stopStream(), [stopStream]);

  useEffect(() => {
    if (recState !== "recording") return;
    setElapsed(0);
    const id = window.setInterval(() => {
      setElapsed((s) => {
        const next = s + 1;
        if (next >= VIDEO_MAX_SECONDS) {
          window.setTimeout(() => {
            if (recorderRef.current?.state === "recording") {
              recorderRef.current.stop();
            }
          }, 0);
        }
        return next;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [recState]);

  const liveActive = recState === "preview" || recState === "recording";

  useEffect(() => {
    if (!liveActive) return;
    const video = liveRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;
    video.srcObject = stream;
    video.muted = true;
    void video.play().catch(() => {
      /* autoplay can fail briefly; user already granted camera */
    });
  }, [liveActive, recState]);

  const startCamera = async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setRecState("unsupported");
      setError(t("capture.video.unsupported"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 1280 } },
      });
      streamRef.current = stream;
      setRecState("preview");
    } catch {
      setRecState("unsupported");
      setError(t("capture.video.permissionDenied"));
    }
  };

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;
    chunksRef.current = [];
    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
      ? "video/webm;codecs=vp9"
      : MediaRecorder.isTypeSupported("video/webm")
        ? "video/webm"
        : "";
    const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const type = recorder.mimeType || "video/webm";
      const blob = new Blob(chunksRef.current, { type });
      const file = new File([blob], `confirmation-${Date.now()}.webm`, { type });
      void (async () => {
        setBusy(true);
        try {
          if (!userId) {
            setError(t("capture.video.signIn"));
            return;
          }
          const uploaded = await createAndUploadMedia("confirmation_video", file, userId);
          onChange(uploaded);
          const msg = uploadUserMessage(uploaded, t);
          if (uploaded.uploadStatus === "failed") setError(msg || t("capture.video.uploadFailed"));
          else setError(msg);
        } catch (e) {
          setError(
            e instanceof MediaValidationError ? e.message : t("capture.video.saveFailed")
          );
        } finally {
          setBusy(false);
          stopStream();
          setRecState("idle");
        }
      })();
    };
    recorder.start(200);
    setRecState("recording");
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const cancelLive = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    stopStream();
    setRecState("idle");
  };

  const onFile = (file: File | undefined) => {
    if (!file) return;
    void (async () => {
      setBusy(true);
      setError(null);
      cancelLive();
      try {
        if (!userId) {
          setError(t("capture.video.signIn"));
          return;
        }
        const uploaded = await createAndUploadMedia("confirmation_video", file, userId);
        onChange(uploaded);
        const msg = uploadUserMessage(uploaded, t);
        if (uploaded.uploadStatus === "failed") setError(msg || t("capture.video.uploadFailed"));
        else setError(msg);
      } catch (e) {
        setError(e instanceof MediaValidationError ? e.message : t("capture.video.badVideo"));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div className={`capture capture--video${asset || liveActive ? " is-ready" : ""}`}>
      <div
        className={`capture__frame capture__frame--media capture__frame--video${
          asset || liveActive ? "" : " is-empty"
        }`}
      >
        {liveActive ? (
          <>
            <video
              ref={liveRef}
              className="capture__preview capture__preview--live"
              muted
              playsInline
              autoPlay
            />
            {recState === "recording" ? (
              <span className="capture__rec">{t("capture.video.rec", { seconds: elapsed })}</span>
            ) : null}
          </>
        ) : asset ? (
          <video
            className="capture__preview"
            src={asset.publicUrl || asset.objectUrl}
            controls
            playsInline
            preload="metadata"
          />
        ) : null}
      </div>

      <div className="capture__title">
        {asset
          ? t("capture.video.titleReady")
          : liveActive
            ? t("capture.video.titleLive")
            : t("capture.video.titleEmpty")}
      </div>
      <p className="capture__hint">
        {asset
          ? t("capture.video.hintReady", {
              fileName: asset.fileName,
              bytes: formatBytes(asset.sizeBytes),
              saved: asset.storageKey ? t("capture.id.savedSuffix") : "",
            })
          : t("capture.video.hintEmpty")}
      </p>

      {asset ? (
        <span
          className="status-chip"
          title={asset.storageKey ?? t("capture.uploadStatus.awaitingUpload")}
        >
          {uploadStatusLabel(asset, t)}
        </span>
      ) : null}

      {error ? <p className="capture__error">{error}</p> : null}

      <div className="capture__actions">
        {!liveActive && !asset ? (
          <>
            <button type="button" className="capture__btn" disabled={busy} onClick={startCamera}>
              {t("capture.video.record")}
            </button>
            <button
              type="button"
              className="capture__btn capture__btn--ghost"
              disabled={busy}
              onClick={() => cameraFileRef.current?.click()}
            >
              {t("capture.video.cameraUpload")}
            </button>
            <button
              type="button"
              className="capture__btn capture__btn--ghost"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              {t("capture.video.chooseFile")}
            </button>
          </>
        ) : null}

        {recState === "preview" ? (
          <>
            <button type="button" className="capture__btn" disabled={busy} onClick={startRecording}>
              {t("capture.video.startRecording")}
            </button>
            <button type="button" className="capture__btn capture__btn--ghost" onClick={cancelLive}>
              {t("capture.video.cancel")}
            </button>
          </>
        ) : null}

        {recState === "recording" ? (
          <button type="button" className="capture__btn" onClick={stopRecording}>
            {t("capture.video.stopSave")}
          </button>
        ) : null}

        {asset ? (
          <>
            <button type="button" className="capture__btn" disabled={busy} onClick={startCamera}>
              {t("capture.video.recordAgain")}
            </button>
            <button
              type="button"
              className="capture__btn capture__btn--ghost"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
            >
              {t("capture.video.replaceFile")}
            </button>
            <button
              type="button"
              className="capture__btn capture__btn--danger"
              disabled={busy}
              onClick={() => onChange(null)}
            >
              {t("capture.video.remove")}
            </button>
          </>
        ) : null}
      </div>

      <input
        ref={cameraFileRef}
        className="sr-only"
        type="file"
        accept={VIDEO_ACCEPT}
        capture="user"
        onChange={(e) => {
          onFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <input
        ref={fileRef}
        className="sr-only"
        type="file"
        accept={VIDEO_ACCEPT}
        onChange={(e) => {
          onFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </div>
  );
}
